import axios, { type AxiosInstance } from 'axios'
import { ensurePatientSessionBeforeMutation } from '@/utilities/auth/expireBrowserSession'
import { handlePatientApiFailure } from '@/utilities/auth/handlePatientApiFailure'
import {
  isPatientSessionExpired,
  PatientSessionExpiredError,
} from '@/utilities/auth/patientSession'
import { isClientPortal } from '@/utilities/stytch/config'

const PATIENT_SESSION_RETRY_FLAG = '_patientSessionRetried'

function hasPatientSessionRetried(config: object): boolean {
  const value: unknown = Object.getOwnPropertyDescriptor(config, PATIENT_SESSION_RETRY_FLAG)?.value
  return value === true
}

function markPatientSessionRetried(config: object): void {
  Object.assign(config, { [PATIENT_SESSION_RETRY_FLAG]: true })
}

function shouldBlockPatientRequest(): boolean {
  if (typeof window === 'undefined') return false
  if (isClientPortal()) return false
  return isPatientSessionExpired()
}

/** Rejects later patient Portal API calls and classifies failures into the shared session. */
export function installPatientSessionInterceptors(axiosInstance: AxiosInstance): void {
  axiosInstance.interceptors.request.use((config) => {
    if (shouldBlockPatientRequest()) {
      ensurePatientSessionBeforeMutation()
      return Promise.reject(new PatientSessionExpiredError())
    }
    return config
  })

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      let handleResult
      try {
        handleResult = await handlePatientApiFailure(error)
      } catch {
        throw error
      }

      if (handleResult.outcome === 'session-ended') {
        throw new PatientSessionExpiredError()
      }

      if (handleResult.outcome === 'session-refreshed' && axios.isAxiosError(error)) {
        const config = error.config
        if (config && !hasPatientSessionRetried(config)) {
          markPatientSessionRetried(config)
          return axiosInstance.request(config)
        }
      }

      throw error
    },
  )
}
